import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PredictionList from '../components/PredictionList';
import AccaBuilder from '../components/AccaBuilder';
import SortControls from '../components/SortControls';
import { LogoIcon, LogoText } from '../components/Logo';
import TagInfoPage from '../components/TagInfoPage';
import useResults from '../hooks/useResults';
import usePredictions from '../hooks/usePredictions';
import useFavorites from '../hooks/useFavorites';
import { useAuth } from '../context/AuthContext';
import { getGradeLetter } from '../utils/gradeDisplay';
import { settleUltimateV4 } from '../utils/ultimateSettle';
import { buildAccaLegs, accaBadgeCount, pruneSelected, loadAccaSelected, saveAccaSelected } from '../utils/acca';
import { useTranslation } from 'react-i18next';

function Dashboard() {
  const [historyIndex, setHistoryIndex] = useState({ months: {}, overall_stats: {} });
  const [historyData, setHistoryData] = useState({ btts: [], outcome: [], draw: [], correct_score: [], over25: [], htft: [], ultimate: [], ai_picks: [] });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [activeTab, setActiveTab] = useState('outcome');
  const [activeView, setActiveView] = useState('current');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [sortField, setSortField] = useState('composite_grade');
  const [sortDirection, setSortDirection] = useState('desc');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [oddsFormat, setOddsFormat] = useState('decimal');
  const [showTagInfo, setShowTagInfo] = useState(false);
  const [enabledGrades, setEnabledGrades] = useState({
    'A+': true, 'A': true, 'B': true, 'C': true, 'D': true
  });
  const [enabledDates, setEnabledDates] = useState({});
  const mainContentRef = useRef(null);

  const [liveOnly, setLiveOnly] = useState(false);
  const { results, fetchResults, resultsLoading, hasLiveMatches } = useResults();
  const { predictions, aiPicksSummary, loading, error } = usePredictions();
  const { user, signOut } = useAuth();
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const { favorites, toggleFavorite, clearFavorites, isFavorite, hasFavorites, favoritesCount } = useFavorites();

  // Acca slip selection lives here (not in AccaBuilder, which unmounts on tab
  // switch) and is persisted so it survives reloads.
  const [accaSelected, setAccaSelected] = useState(loadAccaSelected);
  useEffect(() => { saveAccaSelected(accaSelected); }, [accaSelected]);

  // Ticked every 60s so acca leg status (open -> started once kickoff passes)
  // recomputes without needing a fresh predictions/results fetch.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const accaLegs = useMemo(() => buildAccaLegs(predictions, results, now), [predictions, results, now]);
  // Drop selections whose leg is gone from the current batch. Guard against an
  // empty, non-error load (e.g. an expired session or a fetch that raced an
  // in-progress upload): usePredictions can resolve with loading=false, no
  // error, and no generated_at/data yet, which must not be read as "the batch
  // is now empty" and wipe the saved slip.
  useEffect(() => {
    if (loading || error || !predictions?.generated_at || accaLegs.length === 0) return;
    setAccaSelected(prev => pruneSelected(prev, accaLegs));
  }, [loading, error, predictions, accaLegs]);
  const accaHasSelection = accaLegs.some(l => accaSelected[l.key]);
  const accaBadge = useMemo(
    () => accaBadgeCount(predictions, results, accaSelected, now),
    [predictions, results, accaSelected, now]
  );

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Fetch results when predictions load from Supabase
  useEffect(() => {
    if (loading) return;
    const allMatches = [
      ...(predictions.btts || []), ...(predictions.outcome || []),
      ...(predictions.draw || []), ...(predictions.correct_score || []),
      ...(predictions.over25 || []), ...(predictions.htft || []),
      ...(predictions.ultimate || []), ...(predictions.ai_picks || []),
    ];
    if (allMatches.length > 0) {
      fetchResults(allMatches);
    }
  }, [loading, predictions, fetchResults]);

  // Load history index (static files — stays in public/data/)
  useEffect(() => {
    fetch('/data/history-index.json')
      .then(res => res.ok ? res.json() : { months: {}, overall_stats: {} })
      .then(data => {
        setHistoryIndex(data);
        const months = Object.keys(data.months || {}).sort().reverse();
        if (months.length > 0) {
          setSelectedMonth(months[0]);
        }
      })
      .catch(() => setHistoryIndex({ months: {}, overall_stats: {} }));
  }, []);

  // Load history data when month changes
  useEffect(() => {
    if (activeView === 'history' && selectedMonth) {
      setHistoryLoading(true);
      fetch(`/data/history-${selectedMonth}.json`)
        .then(res => res.ok ? res.json() : { btts: [], outcome: [], draw: [], correct_score: [], over25: [], htft: [], ultimate: [], ai_picks: [] })
        .then(data => {
          setHistoryData(data);
          setHistoryLoading(false);
        })
        .catch(() => {
          setHistoryData({ btts: [], outcome: [], draw: [], correct_score: [], over25: [], htft: [], ultimate: [], ai_picks: [] });
          setHistoryLoading(false);
        });
    }
  }, [activeView, selectedMonth]);

  // Get available months sorted
  const availableMonths = useMemo(() => {
    return Object.keys(historyIndex.months || {}).sort().reverse();
  }, [historyIndex]);

  // Helper to extract date string from date_time
  const extractDateKey = (dateTime) => {
    if (!dateTime) return null;
    // Handle "Feb 06 2026 - 3:00pm" format
    const match = dateTime.match(/^([A-Za-z]{3}\s+\d{1,2})/);
    if (match) return match[1];
    // Handle ISO format
    if (dateTime.includes('T') || dateTime.includes('-')) {
      const d = new Date(dateTime);
      if (!isNaN(d)) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    return null;
  };

  // Get available dates from ALL current predictions (persists across tabs)
  const availableDates = useMemo(() => {
    if (activeView !== 'current') return [];
    const allPreds = [
      ...(predictions.btts || []),
      ...(predictions.outcome || []),
      ...(predictions.draw || []),
      ...(predictions.correct_score || []),
      ...(predictions.over25 || []),
      ...(predictions.htft || []),
      ...(predictions.ultimate || []),
      ...(predictions.ai_picks || []),
    ];
    const dates = new Set();
    allPreds.forEach(p => {
      const dateKey = extractDateKey(p.date_time);
      if (dateKey) dates.add(dateKey);
    });
    const currentYear = new Date().getFullYear();
    return Array.from(dates).sort((a, b) => {
      const dateA = new Date(a + ' ' + currentYear);
      const dateB = new Date(b + ' ' + currentYear);
      return dateA - dateB;
    });
  }, [predictions, activeView]);

  // Build favorites list from all prediction types
  const favoritesPredictions = useMemo(() => {
    if (!hasFavorites || activeView !== 'current') return [];
    const typeMap = {
      btts: predictions.btts || [],
      outcome: predictions.outcome || [],
      draw: predictions.draw || [],
      correct_score: predictions.correct_score || [],
      over25: predictions.over25 || [],
      htft: predictions.htft || [],
      ultimate: predictions.ultimate || [],
      ai_picks: predictions.ai_picks || [],
    };
    const result = [];
    for (const fav of favorites) {
      const list = typeMap[fav.type];
      if (!list) continue;
      const pred = list.find(p => p.id === fav.id);
      if (pred) {
        result.push({ ...pred, _originalType: fav.type });
      }
    }
    return result;
  }, [favorites, predictions, hasFavorites, activeView]);

  // Auto-switch away from favorites tab when empty
  useEffect(() => {
    if (activeTab === 'favorites' && !hasFavorites) {
      setActiveTab('outcome');
    }
  }, [hasFavorites, activeTab]);

  // Auto-switch away from acca tab if the user navigates to history (acca is current-only)
  useEffect(() => {
    if (activeTab === 'acca' && activeView !== 'current') {
      setActiveTab('outcome');
    }
  }, [activeView, activeTab]);

  // Initialize enabledDates when availableDates changes
  useEffect(() => {
    if (availableDates.length > 0 && Object.keys(enabledDates).length === 0) {
      const initial = {};
      availableDates.forEach(d => { initial[d] = true; });
      setEnabledDates(initial);
    }
  }, [availableDates]);

  // Get current data based on view
  const currentData = activeView === 'current' ? predictions : historyData;
  const currentPredictions = activeTab === 'favorites'
    ? favoritesPredictions
    : activeTab === 'btts'
      ? (currentData.btts || [])
      : activeTab === 'draw'
        ? (currentData.draw || [])
        : activeTab === 'correct_score'
          ? (currentData.correct_score || [])
          : activeTab === 'over25'
            ? (currentData.over25 || [])
            : activeTab === 'htft'
              ? (currentData.htft || [])
              : activeTab === 'ultimate'
                ? (currentData.ultimate || [])
                : activeTab === 'ai_picks'
                  ? (currentData.ai_picks || [])
                  : (currentData.outcome || []);

  // Get unique categories and leagues
  const categories = useMemo(() => {
    const allCats = new Set();
    currentPredictions.forEach(p => {
      if (p.categories && Array.isArray(p.categories)) {
        p.categories.forEach(cat => allCats.add(cat));
      } else if (p.category) {
        allCats.add(p.category);
      }
    });
    return ['all', ...Array.from(allCats)].sort();
  }, [currentPredictions]);

  const leagues = useMemo(() => {
    const lgs = ['all', ...new Set(currentPredictions.map(p => p.league).filter(Boolean))];
    return lgs.sort();
  }, [currentPredictions]);

  // Filter predictions
  const filteredPredictions = useMemo(() => {
    return currentPredictions.filter(p => {
      // Live-only filter: only show matches currently in play
      if (liveOnly) {
        const status = results[p.id]?.status || p.result?.status;
        if (status !== 'IN_PLAY') return false;
      }

      if (selectedCategory !== 'all') {
        const predCategories = p.categories || [p.category];
        if (!predCategories.includes(selectedCategory)) return false;
      }
      if (selectedLeague !== 'all' && p.league !== selectedLeague) return false;

      // Filter by enabled grades
      const gradeLetter = p.grade_letter || getGradeLetter(p.composite_grade || 0, p.type);
      if (!enabledGrades[gradeLetter]) return false;

      // Filter by enabled dates (only in current view)
      if (activeView === 'current' && Object.keys(enabledDates).length > 0) {
        const dateKey = extractDateKey(p.date_time);
        if (dateKey && !enabledDates[dateKey]) return false;
      }

      return true;
    });
  }, [currentPredictions, selectedCategory, selectedLeague, enabledGrades, activeTab, enabledDates, activeView, liveOnly, results]);

  // Sort predictions
  const sortedPredictions = useMemo(() => {
    const sorted = [...filteredPredictions].sort((a, b) => {
      let aVal, bVal;

      // Handle nested field paths (e.g. "quality_signals.draw_tendency")
      if (sortField.includes('.')) {
        aVal = sortField.split('.').reduce((o, k) => o?.[k], a);
        bVal = sortField.split('.').reduce((o, k) => o?.[k], b);
      } else {
        aVal = a[sortField];
        bVal = b[sortField];
      }

      if (sortField === 'date_time') {
        aVal = parseDateTimeToTimestamp(aVal);
        bVal = parseDateTimeToTimestamp(bVal);
      }

      // Handle composite_grade sorting (pre-computed field)
      if (sortField === 'composite_grade') {
        aVal = a.composite_grade || 0;
        bVal = b.composite_grade || 0;
      }

      let result = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        result = sortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      } else if (typeof aVal === 'string' && typeof bVal === 'string') {
        result = sortDirection === 'desc' ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
      }

      // Tiebreaker: when primary sort values are equal, sort by date then by league
      if (result === 0) {
        const aTime = parseDateTimeToTimestamp(a.date_time);
        const bTime = parseDateTimeToTimestamp(b.date_time);
        if (aTime !== bTime) return aTime - bTime; // Earlier matches first
        // Final tiebreaker: league name for deterministic order
        return (a.league || '').localeCompare(b.league || '');
      }

      return result;
    });
    return sorted;
  }, [filteredPredictions, sortField, sortDirection, activeTab]);

  // Merge results with predictions - prefer FINISHED results
  const predictionsWithResults = useMemo(() => {
    return sortedPredictions.map(p => {
      const apiResult = results[p.id];
      const jsonResult = p.result;

      // Use API result only if it has a meaningful status
      if (apiResult && apiResult.status && apiResult.status !== 'SCHEDULED') {
        return { ...p, result: apiResult };
      }
      // Otherwise use JSON result
      return { ...p, result: jsonResult || null };
    });
  }, [sortedPredictions, results]);

  // Calculate stats - always from filtered predictions (respects grade filter)
  const stats = useMemo(() => {
    // If any grade is disabled, recalculate from filtered predictions
    const hasGradeFilter = Object.values(enabledGrades).some(v => !v);

    if (activeView === 'history' && selectedMonth && historyIndex.months[selectedMonth] && !hasGradeFilter) {
      // Use pre-calculated stats only if no grade filter is active
      const monthStats = historyIndex.months[selectedMonth];
      const typeStats = activeTab === 'btts' ? monthStats.btts_stats : activeTab === 'draw' ? monthStats.draw_stats : activeTab === 'correct_score' ? monthStats.correct_score_stats : activeTab === 'over25' ? monthStats.over25_stats : activeTab === 'htft' ? monthStats.htft_stats : activeTab === 'ultimate' ? monthStats.ultimate_stats : activeTab === 'ai_picks' ? monthStats.ai_picks_stats : monthStats.outcome_stats;
      return typeStats || { total: 0, completed: 0, wins: 0 };
    }
    // Calculate from filtered predictions
    if (activeTab === 'favorites') {
      return calculateFavoritesStats(predictionsWithResults);
    }
    return calculateStats(predictionsWithResults, activeTab);
  }, [predictionsWithResults, activeTab, activeView, selectedMonth, historyIndex, enabledGrades]);

  // Overall stats for history
  const overallStats = useMemo(() => {
    if (historyIndex.overall_stats) {
      return historyIndex.overall_stats[activeTab] || null;
    }
    return null;
  }, [historyIndex, activeTab]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedCategory('all');
    // Keep grade and date filters when switching tabs
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setSelectedCategory('all');
    setSelectedLeague('all');
  };

  const handleGradeToggle = (grade) => {
    setEnabledGrades(prev => ({
      ...prev,
      [grade]: !prev[grade]
    }));
  };

  const handleDateToggle = (date) => {
    setEnabledDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-anthracite-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-400">{t('dashboard:loadingPredictions')}</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-anthracite-950 flex items-center justify-center">
        <div className="text-center">
          <div className="text-loss text-xl mb-2">{t('common:error')}</div>
          <div className="text-gray-400">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-anthracite-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-anthracite-900/95 backdrop-blur-sm border-b border-anthracite-700">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-anthracite-800 text-gray-400 hover:text-white transition-colors lg:hidden"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex items-center gap-3">
              <LogoIcon className="w-10 h-10" />
              <div>
                <LogoText size="text-xl" />
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('common:aiFootballPredictions')}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-anthracite-800 rounded-xl p-1">
              <button
                onClick={() => handleViewChange('current')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeView === 'current'
                    ? 'bg-accent text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('dashboard:current')}
              </button>
              <button
                onClick={() => handleViewChange('history')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeView === 'history'
                    ? 'bg-accent text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('dashboard:history')}
              </button>
            </div>

          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-t border-anthracite-800 overflow-x-auto overflow-y-hidden scrollbar-hide">
          <div className="flex gap-4 sm:gap-8 min-w-max">
            {hasFavorites && activeView === 'current' && (
              <button
                onClick={() => handleTabChange('favorites')}
                className={`tab-item py-4 text-sm font-medium whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === 'favorites' ? 'active text-accent' : 'text-gray-400'
                }`}
              >
                <svg className="w-4 h-4 text-yellow-400 fill-yellow-400" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <span className="hidden sm:inline">{t('dashboard:favorites')}</span>
                <span className="sm:hidden">{t('dashboard:favs')}</span>
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-yellow-400/20 text-yellow-400 text-xs">
                  {favoritesCount}
                </span>
              </button>
            )}
            <button
              onClick={() => handleTabChange('outcome')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'outcome' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:matchOutcome')}</span>
              <span className="sm:hidden">{t('dashboard:outcome')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.outcome?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('draw')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'draw' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              {t('dashboard:draw')}
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.draw?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('btts')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'btts' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:bttsPredictions')}</span>
              <span className="sm:hidden">{t('dashboard:btts')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.btts?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('over25')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'over25' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:over25Goals')}</span>
              <span className="sm:hidden">{t('dashboard:o25')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.over25?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('htft')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'htft' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:htftPredictions')}</span>
              <span className="sm:hidden">{t('dashboard:htft')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.htft?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('ultimate')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'ultimate' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:ultimatePicks')}</span>
              <span className="sm:hidden">{t('dashboard:ultimate')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.ultimate?.length || 0}
              </span>
            </button>
            <button
              onClick={() => handleTabChange('ai_picks')}
              className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                activeTab === 'ai_picks' ? 'active text-accent' : 'text-gray-400'
              }`}
            >
              <span className="hidden sm:inline">{t('dashboard:aiPicks')}</span>
              <span className="sm:hidden">{t('dashboard:ai')}</span>
              <span className="ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full bg-anthracite-800 text-xs">
                {currentData.ai_picks?.length || 0}
              </span>
            </button>
            {activeView === 'current' && (
              <button
                onClick={() => handleTabChange('acca')}
                className={`tab-item py-4 text-sm font-medium whitespace-nowrap ${
                  activeTab === 'acca' ? 'active text-accent' : 'text-gray-400'
                }`}
              >
                {t('dashboard:acca')}
                <span className={`ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${accaHasSelection ? 'bg-accent/20 text-accent' : 'bg-anthracite-800'}`}>
                  {accaBadge}
                </span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 pt-40 h-screen overflow-y-auto z-30 w-64 sidebar">
        <Sidebar
          categories={categories}
          leagues={leagues}
          selectedCategory={selectedCategory}
          selectedLeague={selectedLeague}
          onCategoryChange={setSelectedCategory}
          onLeagueChange={setSelectedLeague}
          stats={stats}
          oddsFormat={oddsFormat}
          onOddsFormatChange={setOddsFormat}
          onShowTagInfo={() => setShowTagInfo(true)}
          user={user}
          onLogout={handleLogout}
          enabledGrades={enabledGrades}
          onGradeToggle={handleGradeToggle}
        />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)}></div>
          <div className="absolute left-0 top-36 bottom-0 w-64 bg-anthracite-900 overflow-y-auto rounded-tr-xl pt-4">
            <Sidebar
              categories={categories}
              leagues={leagues}
              selectedCategory={selectedCategory}
              selectedLeague={selectedLeague}
              onCategoryChange={(cat) => { setSelectedCategory(cat); setSidebarOpen(false); }}
              onLeagueChange={(lg) => { setSelectedLeague(lg); setSidebarOpen(false); }}
              stats={stats}
              oddsFormat={oddsFormat}
              onOddsFormatChange={setOddsFormat}
              onShowTagInfo={() => { setShowTagInfo(true); setSidebarOpen(false); }}
              user={user}
              onLogout={handleLogout}
              enabledGrades={enabledGrades}
              onGradeToggle={(grade) => { handleGradeToggle(grade); setSidebarOpen(false); }}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-[180px] md:pt-40 pb-12 transition-all lg:ml-64">
        <div className="px-6">
          {/* History Month Selector */}
          {activeView === 'history' && (
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4">
                {/* Month selector */}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{t('dashboard:month')}</span>
                  <div className="flex flex-wrap gap-2">
                    {availableMonths.map(month => (
                      <button
                        key={month}
                        onClick={() => setSelectedMonth(month)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedMonth === month
                            ? 'bg-accent text-white'
                            : 'bg-anthracite-800 text-gray-400 hover:text-white border border-anthracite-700'
                        }`}
                      >
                        {formatMonth(month)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Overall stats */}
                {overallStats && (
                  <div className="ml-auto flex items-center gap-4 text-sm">
                    <span className="text-gray-500">{t('dashboard:allTime')}</span>
                    <span className="text-white">{overallStats.total} {t('dashboard:picks')}</span>
                    <span className={overallStats.win_rate >= 50 ? 'text-win' : 'text-loss'}>
                      {t('dashboard:winRateLabel', { rate: overallStats.win_rate })}
                    </span>
                  </div>
                )}
              </div>

              {/* Month stats */}
              {selectedMonth && historyIndex.months[selectedMonth] && (
                <div className="mt-4 p-4 bg-anthracite-800/50 rounded-xl border border-anthracite-700">
                  <div className="flex flex-wrap gap-6 text-sm">
                    <div>
                      <span className="text-gray-500">{t('dashboard:totalPicks')}</span>
                      <span className="text-white ml-2">{stats.total}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('dashboard:completed')}:</span>
                      <span className="text-white ml-2">{stats.completed}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('dashboard:wins')}:</span>
                      <span className="text-win ml-2">{stats.wins}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('dashboard:winRate')}:</span>
                      <span className={`ml-2 ${stats.win_rate >= 50 ? 'text-win' : 'text-loss'}`}>
                        {stats.win_rate || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'acca' ? (
            <AccaBuilder
              predictions={predictions}
              results={results}
              selected={accaSelected}
              setSelected={setAccaSelected}
              now={now}
            />
          ) : (
          <>
          {/* Date Filter (current view only) */}
          {activeView === 'current' && availableDates.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {hasLiveMatches && (
                <button
                  onClick={() => setLiveOnly(!liveOnly)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5 ${
                    liveOnly
                      ? 'bg-accent/20 text-accent border-accent/30'
                      : 'bg-anthracite-800 text-gray-400 border-anthracite-700 hover:border-accent/30'
                  }`}
                >
                  <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
                  LIVE
                </button>
              )}
              <span className="text-xs text-gray-500">{t('dashboard:dates')}</span>
              {availableDates.map(date => {
                const isEnabled = enabledDates[date];
                return (
                  <button
                    key={date}
                    onClick={() => handleDateToggle(date)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      isEnabled
                        ? 'bg-accent/20 text-accent border-accent/30'
                        : 'bg-anthracite-800 text-gray-600 border-anthracite-700 opacity-50'
                    }`}
                  >
                    {formatDateLabel(date, i18n.language)}
                  </button>
                );
              })}
              {Object.values(enabledDates).some(v => !v) && (
                <button
                  onClick={() => availableDates.forEach(d => !enabledDates[d] && handleDateToggle(d))}
                  className="text-xs text-gray-500 hover:text-accent ml-2"
                >
                  {t('common:reset')}
                </button>
              )}
            </div>
          )}

          {/* Sort Controls */}
          <div className="mb-6 flex items-center justify-between">
            <SortControls
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              type={activeTab}
            />
            {activeTab === 'favorites' && (
              <button
                onClick={clearFavorites}
                className="px-3 py-2 rounded-lg text-xs font-medium text-gray-400 hover:text-loss border border-anthracite-700 hover:border-loss/30 transition-colors"
              >
                {t('dashboard:clearAllFavorites')}
              </button>
            )}
          </div>

          {/* Active Filters */}
          {(selectedCategory !== 'all' || selectedLeague !== 'all') && (
            <div className="flex items-center gap-2 mb-6">
              <span className="text-xs text-gray-500">{t('dashboard:activeFilters')}</span>
              {selectedCategory !== 'all' && (
                <span className="px-3 py-1 rounded-full bg-accent/20 text-accent text-xs flex items-center gap-2">
                  {selectedCategory.replace(/^\d+_/, '').replace(/_/g, ' ')}
                  <button onClick={() => setSelectedCategory('all')} className="hover:text-white">×</button>
                </span>
              )}
              {selectedLeague !== 'all' && (
                <span className="px-3 py-1 rounded-full bg-accent/20 text-accent text-xs flex items-center gap-2">
                  {selectedLeague}
                  <button onClick={() => setSelectedLeague('all')} className="hover:text-white">×</button>
                </span>
              )}
              <button
                onClick={() => { setSelectedCategory('all'); setSelectedLeague('all'); }}
                className="text-xs text-gray-500 hover:text-accent"
              >
                {t('dashboard:clearAll')}
              </button>
            </div>
          )}

          {/* Results Count */}
          <div className="mb-4 text-sm text-gray-500">
            {t('dashboard:showing', { count: predictionsWithResults.length })}
            {activeView === 'history' && selectedMonth && ` ${t('dashboard:from', { month: formatMonth(selectedMonth) })}`}
          </div>

          {/* Predictions List */}
          <PredictionList
            predictions={predictionsWithResults}
            type={activeTab}
            oddsFormat={oddsFormat}
            isFavorite={activeView === 'current' ? isFavorite : undefined}
            onToggleFavorite={activeView === 'current' ? toggleFavorite : undefined}
          />
          </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-anthracite-800 py-6 lg:ml-64">
        <div className="px-6 text-center text-xs text-gray-600">
          <p>{t('common:lastUpdated', { date: predictions.generated_at ? new Date(predictions.generated_at).toLocaleString() : 'Unknown' })}</p>
          <p className="mt-1">{t('common:informationalOnly')}</p>
          <p className="mt-2 space-x-3">
            <Link to="/terms" className="hover:text-accent transition-colors">{t('common:termsAndConditions')}</Link>
            <span>&middot;</span>
            <Link to="/privacy" className="hover:text-accent transition-colors">{t('common:privacyPolicy')}</Link>
          </p>
        </div>
      </footer>

      {/* Tag Info Modal */}
      {showTagInfo && <TagInfoPage onClose={() => setShowTagInfo(false)} />}
    </div>
  );
}

function calculateFavoritesStats(predictions) {
  const total = predictions.length;
  const withResults = predictions.filter(p => p.result && p.result.status === 'FINISHED');

  let wins = 0;
  let determined = 0;
  withResults.forEach(p => {
    const type = p._originalType || 'outcome';
    const isWin = checkWin(p, type);
    if (isWin !== null) {
      determined++;
      if (isWin) wins++;
    }
  });

  return {
    total,
    completed: determined,
    wins,
    win_rate: determined > 0 ? Math.round(wins / determined * 100 * 10) / 10 : 0
  };
}

function calculateStats(predictions, type) {
  const total = predictions.length;
  const withResults = predictions.filter(p => p.result && p.result.status === 'FINISHED');

  let wins = 0;
  let determined = 0;
  withResults.forEach(p => {
    const isWin = checkWin(p, type);
    if (isWin !== null) {
      determined++;
      if (isWin) wins++;
    }
  });

  return {
    total,
    completed: determined,
    wins,
    win_rate: determined > 0 ? Math.round(wins / determined * 100 * 10) / 10 : 0
  };
}

function checkWin(prediction, type) {
  if (!prediction.result || prediction.result.status !== 'FINISHED') return null;

  const homeGoals = prediction.result.homeGoals;
  const awayGoals = prediction.result.awayGoals;

  if (type === 'btts') {
    return homeGoals > 0 && awayGoals > 0;
  } else if (type === 'draw') {
    return homeGoals === awayGoals;
  } else if (type === 'over25') {
    return homeGoals + awayGoals >= 3;
  } else if (type === 'htft') {
    const pred = prediction.prediction || '';
    if (pred.includes('/')) {
      const [predHt, predFt] = pred.split('/');
      const actualFt = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
      // If FT doesn't match, it's definitely a loss regardless of HT
      if (predFt !== actualFt) return false;
      // Check HT result if available
      const htHome = prediction.result.htHomeGoals;
      const htAway = prediction.result.htAwayGoals;
      if (htHome != null && htAway != null) {
        const actualHt = htHome > htAway ? 'H' : htHome < htAway ? 'A' : 'D';
        return predHt === actualHt;
      }
      // No HT data but FT matches — can't determine, show as pending
      return null;
    }
    return false;
  } else if (type === 'correct_score') {
    return `${homeGoals}-${awayGoals}` === prediction.predicted_score;
  } else if (type === 'ultimate') {
    const v4Result = settleUltimateV4(prediction, homeGoals, awayGoals);
    if (v4Result !== undefined) return v4Result;
    // New single-pick logic: win/loss judged on primary pick only
    if (prediction.primary_pick === 'draw') {
      return homeGoals === awayGoals;
    }
    if (prediction.primary_pick === 'correct_score') {
      const ps = prediction.cs_data?.predicted_score || prediction.primary_detail;
      return `${homeGoals}-${awayGoals}` === ps;
    }
    // Legacy fallback for old-format picks (no primary_pick field)
    let wins = 0, total = 0;
    if (prediction.btts_prediction) { total++; if (homeGoals > 0 && awayGoals > 0) wins++; }
    if (prediction.outcome_prediction) {
      total++;
      const actual = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
      if (prediction.outcome_prediction.prediction === actual) wins++;
    }
    if (prediction.draw_prediction) { total++; if (homeGoals === awayGoals) wins++; }
    if (prediction.cs_prediction) {
      total++;
      if (`${homeGoals}-${awayGoals}` === prediction.cs_prediction.predicted_score) wins++;
    }
    return total > 0 && wins >= Math.ceil(total / 2);
  } else if (type === 'ai_picks') {
    const st = prediction.source_type;
    if (st === 'btts') return homeGoals > 0 && awayGoals > 0;
    if (st === 'draw') return homeGoals === awayGoals;
    if (st === 'over25') return homeGoals + awayGoals >= 3;
    if (st === 'htft') {
      const rawPred = (prediction.pick_type || '').split('(')[0].trim();
      if (rawPred.includes('/')) {
        const [predHt, predFt] = rawPred.split('/').map(s => s.trim());
        const actualFt = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
        const actualHt = prediction.result?.htHomeGoals != null && prediction.result?.htAwayGoals != null
          ? (prediction.result.htHomeGoals > prediction.result.htAwayGoals ? 'H' : prediction.result.htHomeGoals < prediction.result.htAwayGoals ? 'A' : 'D')
          : null;
        if (actualHt === null) return null;
        return predHt === actualHt && predFt === actualFt;
      }
      return false;
    }
    if (st === 'correct_score') {
      const score = prediction.pick_type?.replace('CS ', '') || '';
      return `${homeGoals}-${awayGoals}` === score;
    }
    if (st === 'ultimate') {
      if (prediction.pick_type === 'Draw') return homeGoals === awayGoals;
      if (prediction.pick_type?.startsWith('CS')) {
        const score = prediction.pick_type.replace('CS ', '').replace('CS: ', '');
        return `${homeGoals}-${awayGoals}` === score;
      }
    }
    const actual = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
    const pickMap = { 'Home Win': 'H', 'Away Win': 'A', 'Draw': 'D' };
    return pickMap[prediction.pick_type] === actual;
  } else {
    const actualResult = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
    return prediction.prediction === actualResult;
  }
}

function formatMonth(monthStr) {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function formatDateLabel(dateKey, lang) {
  // dateKey is like "Feb 10" — parse and reformat in user's locale
  try {
    const currentYear = new Date().getFullYear();
    const d = new Date(dateKey + ' ' + currentYear);
    if (!isNaN(d.getTime())) {
      const locale = lang === 'el' ? 'el-GR' : lang === 'de' ? 'de-DE' : lang === 'es' ? 'es-ES' : lang === 'it' ? 'it-IT' : lang === 'tr' ? 'tr-TR' : 'en-US';
      return d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    }
  } catch {}
  return dateKey;
}

function parseDateTimeToTimestamp(dateTime) {
  if (!dateTime || dateTime === 'TBD') return 0;
  try {
    if (dateTime.includes(' - ')) {
      const [datePart, timePart] = dateTime.split(' - ');
      const timeMatch = timePart.match(/(\d{1,2}):(\d{2})(am|pm)/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1]);
        const minutes = timeMatch[2];
        const period = timeMatch[3].toLowerCase();
        if (period === 'pm' && hours !== 12) hours += 12;
        if (period === 'am' && hours === 12) hours = 0;
        const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes}`;
        const dateObj = new Date(`${datePart} ${formattedTime} GMT`);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.getTime();
        }
      }
    }
    const date = new Date(dateTime);
    return isNaN(date.getTime()) ? 0 : date.getTime();
  } catch {
    return 0;
  }
}

export default Dashboard;
